import { ISectionLogger } from "@multiweb/logging";
import { Event } from "Global/Base/Events";
import { RequestContext } from "OW.Remoting/Remoting/RequestContext";
import { ErrorHandler } from "UI/Errors";
import { convertContextFromFrameToRequest } from "UI/UI/Utilities/SharedUIMethods";
import {
    CryptoExtensionUnavailableException,
    NativeAppExitedException,
    NativeAppUnavailableException,
} from "../../../../Cryptography/Crypto/CryptoExceptions";
import { ISignedInfo, ISigningSession, ISigningStreamCreator } from "../../../../Cryptography/Crypto/CryptoInterfaces";
import { NativeAppStreamReader } from "../../../../Cryptography/Crypto/NativeAppStreamReader";
import { signatureManager } from "../../../../Cryptography/Crypto/SignatureManager";
import { SignatureStreamSettings } from "../../../../Cryptography/Crypto/SignatureStreamSettings";
import { HTTPRequest } from "../../../../Global/Base/Comunication/HTTPRequest";
import { DataConverter } from "../../../../Global/Base/Converters/DataConverter";
import { DigitalSignatureState, SignatureInfoType } from "../../../../Global/Base/Crypto/ISignatureInfo";
import { ISignatureProviderProfile } from "../../../../Global/Base/Crypto/ISignatureProvider";
import { DIContainer } from "../../../../Global/Base/DIContainer";
import { InvalidArgumentException, InvalidOperationException, UserAbortedException } from "../../../../Global/Base/Exceptions";
import { CancelError, CancellationToken } from "../../../../Global/Base/Promises/CancellationTokenSource";
import { EncryptedFileStreamReaderDecorator } from "../../../../Global/Base/Security/EncryptedFileStreamReaderDecorator";
import { Sha512Algorithm } from "../../../../Global/Base/Security/Hash/Sha512Algorithm";
import { HashingFileStreamReader } from "../../../../Global/Base/Security/HashingFileStreamReader";
import { SecurityUtils } from "../../../../Global/Base/Security/SecurityUtils";
import { IFileStreamReader } from "../../../../Global/Base/Streams/IFileStreamReader";
import { FileUtils } from "../../../../Global/Base/Utils/FileUtils";
import { Globalization } from "../../../../Global/Dom/Globalization";
import { UIUtils } from "../../../../Global/Dom/UIUtils";
import { EDMSException } from "../../../../OW.Remoting/Basic/OWExceptions";
import { OWObject } from "../../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { FileUploader, UploadCancelledException } from "../../../../OW.Remoting/Remoting/FileUploader";
import { Localization } from "../../../../OW.Remoting/Remoting/Localization";
import { OWManager } from "../../../../OW.Remoting/Remoting/OWManager";
import { GenericControl } from "../../../UI.BaseControls/GenericControl";
import { FileEditElement } from "../../../UI.Controls/FileEditElement";
import { SimpleFileEditElement } from "../../../UI.Controls/SimpleFileEditElement";
import { IHasFrameContext, IHTMLElementWithControl } from "../Controls/ControlInterfaces";
import { BlockingLayerBuilderDecorator } from "../DataBuilders/BlockingLayerBuilderDecorator";
import { CopyUrlBuilder } from "../DataBuilders/CopyUrlBuilder";
import { IContentBuilder } from "../DataBuilders/IContentBuilder";
import { LocalizedStringBuilder } from "../DataBuilders/LocalizedStringBuilder";
import { SignLayerBuilder } from "../DataBuilders/SignLayerBuilder";
import { DownloadManager } from "../DownloadManager";
import { FileEditProgressStatus, FileEditStatus } from "../FileMenu";
import { IShowArchiveVersionsFormBuilder } from "../FormBuilders/IFormBuilder";
import { FormManager } from "../FormManager/IFormManager";
import { MessageWindow } from "../MessageWindow";
import { UIManager } from "../UIManager";
import { UploadManager } from "../UploadManager";
import { FrameSharedMethods } from "../XGControls/Frames/FrameSharedMethods";
import { IForm } from "../XGControls/IForm";
import { ModalWindowType } from "../XGControls/ModalWindow";

/**
 * Třída obsahuje logiku prací se soubory
 */
export class FileEditCore {
    private readonly _fileEdit: IFileEdit;
    private readonly _attrName: string;
    private readonly _allowCounterSignature: boolean;
    private readonly _logger: ISectionLogger;

    private __downloadUrl: string;
    private _actualObject: OWObject;
    private _uploader: FileUploader;
    private _status: FileEditStatus;
    private _hashingReader: HashingFileStreamReader;
    private _owner: IHasFrameContext;

    public get actualObject(): OWObject {
        return this._actualObject;
    }

    public set actualObject(val: OWObject) {
        this._actualObject = val;
    }

    private get _downloadUrl(): string {
        return this.__downloadUrl;
    }

    private set _downloadUrl(value: string) {
        if (this.__downloadUrl !== value) {
            this.__downloadUrl = value;
            this.downloadUrlChanged.trigger(this, value);
        }
    }

    public set status(val: FileEditStatus) {
        this._status = val;
    }

    public get downloadUrl(): string {
        return this._downloadUrl;
    }

    public readonly downloadUrlChanged = new Event<this, string>();

    constructor(logger: ISectionLogger, fileEdit: IFileEdit, attrName: string, owner: IHasFrameContext, allowCounterSignature: boolean = true) {
        this._logger = logger;
        this._fileEdit = fileEdit;
        this._attrName = attrName;
        this._allowCounterSignature = allowCounterSignature;
        this._owner = owner;
    }

    //TODO: 33753
    protected _getRequestContext(cancellationToken: CancellationToken = CancellationToken.none): RequestContext {
        return convertContextFromFrameToRequest(this._owner.frameContext, cancellationToken);
    }

    /** Zkopíruje odkaz či zobrazí v případě dokumentu a archivní verze okno pro vykopírování odkazů ke stažení */
    public copyUrl(): void {
        const url = OWManager.getPublicDocumentDownloadUrl(this._actualObject.classInfo.className, this._actualObject.id, this._attrName);

        if (this._fileEdit.getArchiveDocumentId()) {
            const message = new MessageWindow();
            message.header = "GUI-667747"; // Kopírovat odkaz na soubor
            message.okButtonCaption = "GUI-4447"; //Zavřít
            message.contentBuilder = new CopyUrlBuilder([
                { url: url, titleLocKey: "GUI-460106" }, // Originální dokument
                { url: OWManager.getPublicDocumentDownloadUrl(this._actualObject.classInfo.className, this._actualObject.id, this._attrName, true), titleLocKey: "GUI-460096" }]); // Archivní dokument

            message.show();

            return;
        }

        UIUtils.pasteToClipboard(url);
    }

    /**
     * Zobrazí archivní verze dokumentu
     */
    public async showArchiveVersions(ownerControl: HTMLElement): Promise<void> {
        const parentFrame = GenericControl.findParent(ownerControl.parentElement, "[data-behaviour=form]") as IHTMLElementWithControl;
        const ownerForm = (parentFrame && parentFrame.tscControl instanceof IForm) ? parentFrame.tscControl : undefined;
        const formBuilder = DIContainer.instance.create(IShowArchiveVersionsFormBuilder);
        const form = await formBuilder.build(this.actualObject.classInfo.className, this.actualObject.id, this.actualObject.instanceID, this._attrName, { ownerForm }, this._owner.frameContext);
        FormManager.instance.openFormModal({ form: form, type: ModalWindowType.parametricForm, showBreadcrumbNav: true, parametricData: { formContext: ownerForm?.formContext } });
    }

    public async isAttributeEncrypted(): Promise<boolean> {
        return this._actualObject != null && await this._actualObject.isAttributeEncrypted(this._attrName);
    }

    /**
     * Vyvolá stažení dokumentu
     * @param isArchiveDocument Určuje, zda má být stahována archivní verze
     */
    public async downloadFile(isArchiveDocument?: boolean): Promise<void> {
        if (this._actualObject == null)
            return;

        // TODO: #12428 - časem se dodělá: načtení metadat, antivirová kontrola a případná interakce s uživatelem (pokud byl nalezen vir nebo soubor nešlo zkontrolovat, tak uživatel potvrzuje, že chce i přes to soubor stáhnout)

        // pokud je šifrovaný atribut, tak se musí stažený soubor nejdříve dešifrovat
        if (await this.isAttributeEncrypted()) {
            try {
                UIManager.instance.showBlockingLayer(new BlockingLayerBuilderDecorator(new LocalizedStringBuilder("GUI-634025", [this._fileEdit.getFileName()]))); // Váš soubor '@0' je připravován...

                const decryptedFile = await this.downloadAndDecryptFile(isArchiveDocument);
                if (decryptedFile != null)
                    FileUtils.saveFileAs(this._fileEdit.getFileName(), decryptedFile);
            } finally {
                UIManager.instance.hideBlockingLayer();
            }
        } else {
            await DownloadManager.downloadFile(this._actualObject.instanceID, this._attrName, isArchiveDocument ? this._fileEdit.getArchiveDocumentId() : "", () => { /* Prázdná arrow funkce */ }, this._getRequestContext());
        }
    }

    /**
    * Získá URL pro stažení souboru a tím zároveň ověří, zda je soubor dostupný
    * @param isArchiveDocument Určuje, zda má být získána URL archivní verze dokumentu
    */
    public async updateDownloadUrl(isArchiveDocument?: boolean): Promise<void> {
        if (this._actualObject?.instanceID == null) {
            return;
        }

        try {
            this._downloadUrl = await OWManager.getDocumentDownloadUrl(this._actualObject.instanceID, this._attrName, isArchiveDocument ? this._fileEdit.getArchiveDocumentId() : "", this._getRequestContext());
        } catch (error) {
            this.handleError(error, error instanceof EDMSException, true);
        }
    }

    /** Vymaže downloadUrl */
    public clearDownloadUrl(): void {
        this._downloadUrl = null;
    }

    /**
     * Stáhne soubor do paměti a dešifruje ho
     * @param isArchiveDocument Určuje, zda má být stahována archivní verze
     */
    public async downloadAndDecryptFile(isArchiveDocument?: boolean): Promise<Blob | null> {
        try {
            /**
             * Zatím vytvořena naivní implementace, která dělá všecho postupně - nejdřív stáhne celý soubor, pak ho celý dešifruje a pak nabídne uložení
             * Stažení a dešifrování by se dalo dělat po částech, takže to by se dalo časem optimalizovat. Otázka je, zda bude to zrychlení kvůli více dotazům vůbec znát.
             * Uložení se musí udělat až najednou celého souboru. Po částech není k file systemu přístup.
             */
            if ((this._fileEdit.getFileName() && !this._downloadUrl) || isArchiveDocument) {
                await this.updateDownloadUrl(isArchiveDocument);
            }

            if (!(await DownloadManager.prepareDownload(this._downloadUrl))) {
                return null;
            }

            const encryptedFile = HTTPRequest.get(this._downloadUrl, "arraybuffer");
            if (encryptedFile == null)
                throw new InvalidOperationException(`Unable to load file from object '${this._actualObject.objectName}' id '${this._actualObject.id}' and attribute '${this._attrName}'`); //`

            const key = await this._actualObject.getEncryptionKey();
            if (key == null)
                throw new InvalidOperationException("Unable to download encrypted file without key");

            const decryptor = SecurityUtils.getStreamDecryptor(key);
            if (decryptor == null)
                throw new InvalidOperationException(`Unable to find decryptor for key '${key.algorithm.name}'`); //`

            return new Blob([await decryptor.decryptBlock(await encryptedFile, true)] /*, { type: 'application/octet-binary' }*/);
        } catch (error) {
            this.handleError(error);
            return null;
        }
    }

    public async uploadFile(streamReader: IFileStreamReader, sign: boolean): Promise<boolean> {
        let hashProvider: () => string;
        if (await this.isAttributeEncrypted()) {
            if (await this._actualObject.getEncryptionKey() == null && !(await FrameSharedMethods.showEncryptDecryptDialog(this._actualObject, true))) {
                this._fileEdit.setStatus(FileEditStatus.empty);
                return false;
            }
        }

        try {
            if (sign) {
                return this.createSignature(streamReader, true);
            } else {
                await this._upload(streamReader, hashProvider);
                await this._actualObject.waitForUpdateInstance();
            }
            return true;
        } catch (e) {
            await this.handleError(e);
            return false;
        }
    }

    private async _upload(streamReader: IFileStreamReader, hashProvider?: () => string): Promise<void> {
        if (this._fileEdit.fileEditElement && this._fileEdit.fileEditElement instanceof FileEditElement)
            this._fileEdit.fileEditElement.updateSignatureButton([]); //Probíhá upload souboru, neměl by být zobrazen cryptoLabel, dokud neproběhne upload a zjištění stavu podpisu.
        if (await this.isAttributeEncrypted()) {
            // původní stream reader se obalí dekorátorem, který šifruje data streamu při jeho čtení
            streamReader = await this._createEncryptedStreamReader(streamReader);
            const hasher = this._hashingReader; // closure
            hashProvider = () => DataConverter.bufferToBase64(hasher.hash);
        }

        return await UploadManager.instance.startUpload(streamReader, this._actualObject, this._attrName, hashProvider, this._getRequestContext());
    }

    private async _createEncryptedStreamReader(streamReader: IFileStreamReader): Promise<IFileStreamReader> {
        if (!streamReader)
            throw new InvalidArgumentException("streamReader");
        if (!this._actualObject)
            throw new InvalidOperationException("actualObject is null");

        if (streamReader instanceof EncryptedFileStreamReaderDecorator)
            return streamReader;

        this._hashingReader = new HashingFileStreamReader(streamReader, new Sha512Algorithm());

        return new EncryptedFileStreamReaderDecorator(this._hashingReader, SecurityUtils.getStreamEncryptor(await this._actualObject.getEncryptionKey()));
    }

    /**
     * Ošetřuje chyby při práci se soubory
     * @param error obdržená chyba
     * @param forceMessageWindow zda má být zobrazeno okno s chybovou zprávou
     * @param fileUnavailable zda se jedná o chybu existujícího souboru (např. je nedostupný nebo neexistuje)
     */
    public async handleError(error: Error, forceMessageWindow: boolean = false, fileUnavailable = false): Promise<void> {
        let message: string;

        if (forceMessageWindow) {
            if (fileUnavailable) { // Dokument existuje, ale je nedostupný
                this._fileEdit.setStatus(FileEditStatus.unavailable);
            }

            ErrorHandler.instance.handleError(error);
            return;
        }

        if (error instanceof UploadCancelledException || error instanceof UserAbortedException || error instanceof CancelError) {
            return;
        }

        if (error.message.startsWith("ERROR")) {
            // pro známé chybové kódy se načtou lokalizované hlášky
            let locKey: string = null;
            switch (error.message) {
                case "ERROR MD5":
                    locKey = "GUI-602441";
                    break;
                case "ERROR NotFound":
                    locKey = "GUI-373824";
                    break;
                case "ERROR FileExtension":
                    locKey = "GUI-469050";
                    break;
                default:
                    this._logger.warn(`Unknown upload error code '${error.message}'`); //`
                    break;
            }
            if (locKey) {
                message = await Localization.getTextPromise(locKey);
            } else {
                message = error.toString();
            }
        } else if (error instanceof EDMSException) {
            message = error.locKey ? await Localization.getTextPromise(error.locKey) : error.message;
        } else {
            ErrorHandler.instance.handleError(error);
            //Pokud není dostupná nativka nebo extension, tak se zobrazuje hláška a FE se vrátí do původního validního stavu.
            if (error instanceof NativeAppExitedException || error instanceof NativeAppUnavailableException ||
                error instanceof CryptoExtensionUnavailableException) {
                this._fileEdit.setValid();
                //v případě že byl soubor rovnou nahrán a podepisován je potřeba zrušit i upload. Pokud byl soubor nejprve nahrán a pak podepisován, musí FE zůstat ve stavu s nahrátým souborem.
                if (this._uploader && this._status !== FileEditStatus.download)
                    this._uploader.onCancel.trigger(this._uploader, null);
                return;
            }
        }

        if (this._actualObject != null) {
            const data = UploadManager.instance.getUploadData(this._actualObject.instanceID, this._attrName);
            if (data)
                data.lastError = message; // uloží chybovou zprávu pro příští zobrazení komponenty
        }

        await this._fileEdit.updateCanSign();
    }

    //------------------------------------------------------
    // Podpisová část
    //------------------------------------------------------

    /**
     * Vytvoří podpis a odešle ho na server
     * @param streamReader obsahuje originální nepodepsaný soubor. Používá se v případě nahrát a podepsat, když soubor ještě není v DMS.
     * @param withRefresh
     */
    public async createSignature(streamReader?: IFileStreamReader, withRefresh: boolean = true): Promise<boolean> {
        try {
            this._fileEdit.setSigningInProgress(true);

            if (streamReader) {
                await this._upload(streamReader, undefined);

                UIManager.instance.showLoader();
                try {
                    await this._actualObject.waitForUpdateInstance();
                    const signatureProfile = await this._getSignatureProviderProfile();

                    if (signatureProfile.settings.signatureInsideDocument && !this._allowCounterSignature) {
                        const signatureInfo = await OWManager.getDocumentSignatureInfo(this._actualObject.instanceID, this._attrName, this._getRequestContext());
                        if (signatureInfo != null && signatureInfo.type !== SignatureInfoType.empty) { //podpis už byl obsažen v souboru, není možné vytvářet další podpis
                            return false;
                        }
                    }
                } finally {
                    UIManager.instance.hideLoader();
                }
            }

            const signatureProfile = await this._getSignatureProviderProfile();
            const finalSignature: string | null = await this._signFile(signatureProfile);

            await this._actualObject.waitForUpdateInstance();

            if (finalSignature != null)
                return await this._sendSignature(finalSignature, signatureProfile, withRefresh);
            else
                return false;
        } catch (err) {
            this._logger.error(`Failed to sign and upload a file. ${err}`); //`
            return false;
        } finally {
            this._fileEdit.setSigningInProgress(false);
            if (withRefresh)
                this._actualObject.attributeChanged.trigger(this._actualObject, this._attrName); //vrámci eventy se volá i signInforequest
        }
    }

    private async _signFile(signatureProfile: ISignatureProviderProfile): Promise<string | null> {
        let session: ISigningSession;
        try {
            UIManager.instance.showBlockingLayer(this._createSigningBlockingLayerBuilder());

            session = await signatureManager.instance.openSession(Globalization.actualCultureISO639_1);

            const isAttrEncrypted = await this.isAttributeEncrypted();
            const cryptoKey = isAttrEncrypted && await this._actualObject.getEncryptionKey();
            if (isAttrEncrypted && cryptoKey == null)
                throw new InvalidOperationException("Unable to download encrypted file without key");

            const streamCreator = DIContainer.instance.create(ISigningStreamCreator);
            const settings: SignatureStreamSettings = {
                providerSettings: signatureProfile.settings,
                currentSignatureState: this._fileEdit.getDigitalSignatureState(),
                instanceId: this._actualObject.instanceID,
                attrName: this._attrName,
                currentFileStream: this._fileEdit.getLocalFile(),
                cryptoKey: cryptoKey,
            };

            let batchSignedInfo: ISignedInfo[];
            if (this._fileEdit.getDigitalSignatureState() === DigitalSignatureState.empty) {
                const streams = await streamCreator.createSignatureStreams([settings]);
                batchSignedInfo = await session.createDocumentSignatures(signatureProfile.settings, ...streams);
            } else {
                const counterStreams = (await streamCreator.createCounterSignatureStreams([settings], this._getRequestContext()))[0];
                //counterStreams.signatureStream - stream ve kterém je stávající podpis
                //counterStreams.detachedStream - stream s daty v případě detached podpisu
                batchSignedInfo = await session.createCounterSignature(signatureProfile.settings, counterStreams.signatureStream, counterStreams.documentStream);
            }

            const signedInfo = batchSignedInfo[0];

            if (signedInfo.containsDocument) {
                UIManager.instance.hideBlockingLayer();
                //nahraje soubor s attached podpisem
                await this._upload(new NativeAppStreamReader(signedInfo.stream), undefined);
                await signedInfo.stream.begin(); // Rewind streamu
            }

            return DataConverter.bufferToBase64(await new NativeAppStreamReader(signedInfo.stream).read());
        } catch (e) {
            this.handleError(e);
            return null;
        } finally {
            UIManager.instance.hideBlockingLayer();
            if (session)
                session.dispose();
        }
    }

    private _createSigningBlockingLayerBuilder(): IContentBuilder {
        const builder = new SignLayerBuilder(this._fileEdit.getControlValue != null); // Při volbě "Nahrát a podepsat" není dokument ještě nikde nahraný, takže se nezobrazuje ani tlačítko na stažení
        builder.openFileButtonClickEvent.add(() => {
            this.downloadFile(undefined);
        });
        return builder;
    }

    /**
     * Odešle podpis na službu a vrací true pokud byl úspěšně uložen.
     */
    private async _sendSignature(signature: string, signatureProfile: ISignatureProviderProfile, withRefresh: boolean = true): Promise<boolean> {
        try {
            const result = await OWManager.createDocumentSignature(this._actualObject.instanceID, this._attrName, signature, signatureProfile.id,
                signatureProfile.settings.providerType, signatureProfile.settings.providerVersion, this._getRequestContext());
            const [boolResult, locKey] = OWManager.parseSigningResult(result);

            if (!boolResult) {
                await MessageWindow.showAlert(new LocalizedStringBuilder(`GUI-${locKey}`)); //`
            }
            if (withRefresh)
                this._actualObject.refresh();
            return boolResult;
        } catch (e) {
            await this.handleError(e);
            return false;
        }
    }

    private async _getSignatureProviderProfile(): Promise<ISignatureProviderProfile | null> {
        if (this._fileEdit.getSignatureProviderProfile() === undefined) {
            this._fileEdit.setSignatureProviderProfile(await OWManager.getDigitalSignatureSettings(this._actualObject.instanceID, this._attrName, FileUtils.getFileExtension(this._fileEdit.getFileName()), this._getRequestContext()));
        }
        return this._fileEdit.getSignatureProviderProfile();
    }
}

/**
 * Interface obsahuje rozhranní mezi samotnou logikou prací se soubory a komponent FileEdit a FileCellElement, které tuto logiku používají
 */
export interface IFileEdit {
    /**
     * HTMLElement z FileEditu
     */
    fileEditElement: SimpleFileEditElement;

    /**
     * Vrací mázev souboru
     */
    getFileName(): string;
    /**
     * Vrací id archivního dokumentu
     */
    getArchiveDocumentId(): string;
    /**
     * Vrací referenci na právě uploadovaný soubor
     */
    getLocalFile(): File;
    /**
     * Vrací profil s nastavením pro podepisování konkrétního objektu nebo dokumentu
     */
    getSignatureProviderProfile(): ISignatureProviderProfile | null | undefined;
    /**
     * Nastaví profil pro podepisování konkrétního objektu nebo dokumentu
     * @param value Data transfer object s informacemi o profilu s nastavením provideru pro podpisy
     */
    setSignatureProviderProfile(value: ISignatureProviderProfile | null | undefined): void;
    /**
     * Na začátku vytváření podpisu nastavíme na true a na konci na false.
     */
    setSigningInProgress(value: boolean): void;
    /**
     * Vrací informaci o stavu podpisu
     */
    getDigitalSignatureState(): DigitalSignatureState;
    /**
     * Vrací hodnotu FileEditu - informaci o souboru
     */
    getControlValue(): any;
    /**
     * Zjistí, jestli je možné vytvořit podpis a podle toho vyhodnotí vosible položky "Vytvořit podpis" ve file menu
     * @param param
     */
    updateCanSign(param?: any): Promise<void>; //null || ICanSignParams || CancellationToken
    /**
     * Nastaví stav komponenty na validní
     */
    setValid(): void;
    /**
     * Nastaví stav komponenty na nevalidní
     * @param locKey Lokalizační hláška
     * @param message Informace o nevalidnosti
     */
    setInvalid(locKey: string, message?: string): void;
    /**
     * Nastaví stav komponenty
     * @param status Nastavitelné stavy
     */
    setStatus(status: FileEditStatus): void;
    /**
     * Nastavuje progres komponenty dle stavu dokumentu
     * @param type Typ pregresu
     * @param value Hodnota progresu
     */
    changeProgressStatus(type?: FileEditProgressStatus, value?: string | number): void;
}
